/**
 * API client for the PGP dashboards.
 *
 * Mirrors the function surface of the standalone dashboard's api/pgp.js so the
 * ported pages work unchanged, but targets this backend's /api/pgp/* routes and
 * sends the JWT the rest of the app uses.
 *
 * Route mapping vs the reference API:
 *   /api/pgp/credits                 -> same
 *   /api/pgp/agenda                  -> same
 *   /api/pgp/mappings                -> same
 *   /api/pgp/roster                  -> same
 *   /api/pgp/song-mappings           -> same
 *   /api/status/pub-mapping          -> /api/pgp/status/pub-mapping
 *   /api/status/pub-mapping-lead-ar  -> /api/pgp/status/pub-mapping-lead-ar
 *   /api/status/pub-mapping-action   -> /api/pgp/status/pub-mapping-action
 *   /api/pgp/credits/false-flag      -> same
 *
 * Not ported (upstream features this backend does not implement yet):
 *   export-xlsx / export-pdf / export-html, genius-artists search,
 *   mappings/batch-status upload.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

/** Soft cap for agenda / pub mapping browse (search still runs server-side). */
export const PGP_LIST_LIMIT = 2000;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchJson(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      // FormData must keep the browser's multipart boundary, so no JSON type.
      ...(options.body && !(options.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // This backend returns {error}; the reference API returned {detail}.
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

/** Status / Lead A&R / grain vocabularies, so the UI has one source of truth. */
export function fetchPgpOptions() {
  return fetchJson('/api/pgp/options');
}

/** Writer/producer credits on a chart week (Charts tab). */
export function fetchPgpCredits({
  week,
  charts,
  creditType,
  grain,
  includeIgnored,
  q,
  limit,
  offset,
} = {}) {
  const params = new URLSearchParams();
  if (week) params.set('week', week);
  if (Array.isArray(charts)) {
    for (const label of charts) {
      if (label != null && String(label).trim() !== '') {
        params.append('charts', String(label).trim());
      }
    }
  }
  if (creditType) params.set('credit_type', creditType);
  if (grain && grain !== 'person') params.set('grain', grain);
  if (includeIgnored) params.set('include_ignored', '1');
  if (q) params.set('q', q);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/pgp/credits${qs ? `?${qs}` : ''}`);
}

/**
 * Download Charts rows as CSV with the current filters.
 * Resolves to the saved filename.
 */
export async function downloadPgpChartsCsv({
  week,
  charts,
  creditType,
  grain,
  includeIgnored,
  q,
} = {}) {
  const params = new URLSearchParams();
  if (week) params.set('week', week);
  if (Array.isArray(charts)) {
    for (const label of charts) {
      if (label != null && String(label).trim() !== '') {
        params.append('charts', String(label).trim());
      }
    }
  }
  if (creditType) params.set('credit_type', creditType);
  if (grain && grain !== 'person') params.set('grain', grain);
  if (includeIgnored) params.set('include_ignored', '1');
  if (q) params.set('q', q);
  const qs = params.toString();
  return downloadAttachment(
    `/api/pgp/credits/export${qs ? `?${qs}` : ''}`,
    'pgp_charts.csv',
  );
}

/** Shared CSV/attachment download; resolves to the saved filename. */
async function downloadAttachment(path, fallbackName) {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { filename };
}

/** Agenda HTML table markup, for clipboard paste. */
export async function fetchPgpAgendaExportHtml({ q } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  const qs = params.toString();
  const data = await fetchJson(`/api/pgp/agenda/export-html${qs ? `?${qs}` : ''}`);
  const html = data?.html;
  if (!html || typeof html !== 'string') {
    throw new Error('Agenda HTML export returned no content');
  }
  return html;
}

/** Copy the Agenda table to the clipboard as rich HTML (falls back to text). */
export async function copyPgpAgendaHtml({ q } = {}) {
  const html = await fetchPgpAgendaExportHtml({ q });
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' }),
        }),
      ]);
      return { ok: true };
    } catch {
      // Fall through to a text-only write.
    }
  }
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard is not available in this browser');
  }
  await navigator.clipboard.writeText(html);
  return { ok: true };
}

/** Download the Agenda as CSV. */
export async function downloadPgpAgendaCsv({ q } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  const qs = params.toString();
  return downloadAttachment(
    `/api/pgp/agenda/export${qs ? `?${qs}` : ''}`,
    'pgp_agenda.csv',
  );
}

/**
 * Genius artist search for the add-person flow.
 * A 503 means no token is configured; the caller falls back to manual entry.
 */
export function searchGeniusArtists({ q, limit = 8 } = {}) {
  const params = new URLSearchParams();
  params.set('q', String(q || '').trim());
  if (limit != null) params.set('limit', String(limit));
  return fetchJson(`/api/pgp/genius-artists?${params}`);
}

/** Genius song-resolution cache (Genius tab). */
export function fetchPgpSongMappings({ status = 'unresolved', q, limit, offset } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/pgp/song-mappings${qs ? `?${qs}` : ''}`);
}

/** Roster writer/producer credits on recent Genius releases (Roster tab). */
export function fetchPgpRoster({ sinceDays, creditType, q, limit, offset } = {}) {
  const params = new URLSearchParams();
  if (sinceDays != null) params.set('since_days', String(sinceDays));
  if (creditType) params.set('credit_type', creditType);
  if (q) params.set('q', q);
  if (limit != null) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/pgp/roster${qs ? `?${qs}` : ''}`);
}

/** List pub_mappings rows (Mapping tab). */
export function fetchPubMappings({ q, manualOnly = false, limit = PGP_LIST_LIMIT } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (manualOnly) params.set('manual_only', '1');
  if (limit != null) params.set('limit', String(limit));
  const qs = params.toString();
  return fetchJson(`/api/pgp/mappings${qs ? `?${qs}` : ''}`);
}

/** Agenda — Longform / Proposal / Priority / Watching. */
export function fetchPgpAgenda({ q, limit = PGP_LIST_LIMIT, offset } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (limit != null) params.set('limit', String(limit));
  if (offset != null) params.set('offset', String(offset));
  const qs = params.toString();
  return fetchJson(`/api/pgp/agenda${qs ? `?${qs}` : ''}`);
}

/** Numeric id, slug, or genius.com/artists/… URL — sent as-is when non-numeric. */
export function normalizeGeniusArtistRef(value) {
  if (value == null || String(value).trim() === '') return null;
  return String(value).trim().replace(/\.0$/, '');
}

function identityBody({ id, geniusArtistId }) {
  const body = {};
  if (id != null && String(id).trim() !== '') body.id = Number(id);
  if (geniusArtistId != null && String(geniusArtistId).trim() !== '') {
    body.genius_artist_id = Number(geniusArtistId);
  }
  return body;
}

/** Update pub_mappings.status — prefer table id; Genius id is a fallback upsert. */
export function updatePubMappingStatus({ id, geniusArtistId, status, name, geniusUrl }) {
  return fetchJson('/api/pgp/status/pub-mapping', {
    method: 'PATCH',
    body: JSON.stringify({
      ...identityBody({ id, geniusArtistId }),
      status: status === '' ? null : status,
      name: name ?? null,
      genius_url: geniusUrl ?? null,
    }),
  });
}

/** Update pub_mappings.lead_ar — prefer table id; Genius id is a fallback upsert. */
export function updatePubMappingLeadAr({ id, geniusArtistId, leadAr }) {
  return fetchJson('/api/pgp/status/pub-mapping-lead-ar', {
    method: 'PATCH',
    body: JSON.stringify({
      ...identityBody({ id, geniusArtistId }),
      lead_ar: leadAr === '' ? null : leadAr,
    }),
  });
}

/** Update pub_mappings.action (ball in our court). */
export function updatePubMappingAction({ id, geniusArtistId, action }) {
  return fetchJson('/api/pgp/status/pub-mapping-action', {
    method: 'PATCH',
    body: JSON.stringify({
      ...identityBody({ id, geniusArtistId }),
      action: Boolean(action),
    }),
  });
}

/** Mark / clear a Genius credit as inaccurate (False Credit). */
export function updateGeniusCreditFalseFlag({
  id,
  geniusSongId,
  geniusArtistId,
  creditType,
  isFalseCredit = true,
} = {}) {
  return fetchJson('/api/pgp/credits/false-flag', {
    method: 'PATCH',
    body: JSON.stringify({
      id: id != null && String(id).trim() !== '' ? Number(id) : null,
      genius_song_id:
        geniusSongId != null && String(geniusSongId).trim() !== ''
          ? Number(geniusSongId)
          : null,
      genius_artist_id:
        geniusArtistId != null && String(geniusArtistId).trim() !== ''
          ? Number(geniusArtistId)
          : null,
      credit_type: creditType || null,
      is_false_credit: Boolean(isFalseCredit),
    }),
  });
}

/** Manually add a pub_mappings row (Genius id optional). */
export function createPubMapping({
  name,
  geniusArtistId,
  geniusUrl,
  status,
  leadAr,
  lawyer,
  calc,
  committedMoney,
  uncommitted,
  optionMoney,
  notes,
  action,
} = {}) {
  const blankToNull = (v) => (v === '' || v == null ? null : v);
  return fetchJson('/api/pgp/mappings', {
    method: 'POST',
    body: JSON.stringify({
      name: name ?? null,
      genius_artist_id: normalizeGeniusArtistRef(geniusArtistId),
      genius_url: geniusUrl ?? null,
      status: blankToNull(status),
      lead_ar: blankToNull(leadAr),
      lawyer: blankToNull(lawyer),
      calc: blankToNull(calc),
      committed_money: blankToNull(committedMoney),
      uncommitted: blankToNull(uncommitted),
      option_money: blankToNull(optionMoney),
      notes: blankToNull(notes),
      action: Boolean(action),
    }),
  });
}

/**
 * Edit a pub_mappings row by table id.
 *
 * Each field writes only when its ``update*`` flag is set, so a blank value can
 * clear one field without nulling every field the form did not touch.
 */
export function updatePubMapping(
  mappingId,
  {
    name,
    geniusArtistId,
    setGeniusArtistId = false,
    clearGeniusArtistId = false,
    geniusUrl,
    status,
    updateStatus = false,
    leadAr,
    updateLeadAr = false,
    lawyer,
    updateLawyer = false,
    calc,
    updateCalc = false,
    committedMoney,
    updateCommittedMoney = false,
    uncommitted,
    updateUncommitted = false,
    optionMoney,
    updateOptionMoney = false,
    notes,
    updateNotes = false,
  } = {},
) {
  return fetchJson(`/api/pgp/mappings/${Number(mappingId)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      name: name ?? null,
      genius_artist_id: setGeniusArtistId
        ? normalizeGeniusArtistRef(geniusArtistId)
        : null,
      set_genius_artist_id: Boolean(setGeniusArtistId),
      clear_genius_artist_id: Boolean(clearGeniusArtistId),
      genius_url: geniusUrl ?? null,
      status: status === '' ? null : status ?? null,
      update_status: Boolean(updateStatus),
      lead_ar: leadAr === '' ? null : leadAr ?? null,
      update_lead_ar: Boolean(updateLeadAr),
      lawyer: lawyer === '' ? null : lawyer ?? null,
      update_lawyer: Boolean(updateLawyer),
      calc: calc === '' ? null : calc ?? null,
      update_calc: Boolean(updateCalc),
      committed_money: committedMoney === '' ? null : committedMoney ?? null,
      update_committed_money: Boolean(updateCommittedMoney),
      uncommitted: uncommitted === '' ? null : uncommitted ?? null,
      update_uncommitted: Boolean(updateUncommitted),
      option_money: optionMoney === '' ? null : optionMoney ?? null,
      update_option_money: Boolean(updateOptionMoney),
      notes: notes === '' ? null : notes ?? null,
      update_notes: Boolean(updateNotes),
    }),
  });
}

/**
 * Batch-apply pub_mappings statuses from an Excel/CSV upload.
 * Needs a Status column plus Pub Mapping ID or Genius Artist ID.
 */
export function uploadPgpStatusBatch(file) {
  const body = new FormData();
  body.append('file', file);
  // No Content-Type: the browser sets the multipart boundary itself.
  return fetchJson('/api/pgp/mappings/batch-status', { method: 'POST', body });
}

/** Delete a pub_mappings row by table id. */
export function deletePubMapping(mappingId) {
  return fetchJson(`/api/pgp/mappings/${Number(mappingId)}`, { method: 'DELETE' });
}
